"use client";

import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { BrandIcon } from "@/components/brand-icons/brand-icon";
import type { ConnectionTemplate } from "./templates-data";

const AUTH_TYPE_LABELS: Record<string, string> = {
  oauth2_client: "OAuth2",
  jwt: "JWT",
  aws_signature: "AWS Sig V4",
  bearer_token: "Bearer",
  api_key: "API Key",
  basic_auth: "Basic",
  none: "No Auth",
};

interface TemplateCardProps {
  template: ConnectionTemplate;
  onClick: () => void;
}

export function TemplateCard({ template, onClick }: TemplateCardProps) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "group relative flex cursor-pointer flex-col items-center rounded-xl border border-white/[0.06] bg-white/[0.015] p-5 text-center transition-all duration-200",
        "hover:border-white/[0.12] hover:bg-white/[0.04] hover:shadow-lg hover:shadow-black/20",
      )}
    >
      {/* Status indicator */}
      <div className="absolute left-3 top-3 flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-white/20" />
        <span className="text-[10px] text-white/30">Not connected</span>
      </div>

      {/* Setup guide link */}
      {template.setup_guide_url && (
        <a
          href={template.setup_guide_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="absolute right-3 top-3 text-white/20 transition-colors hover:text-teal-400"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}

      {/* Brand Icon */}
      <div className="mt-4 mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-white/[0.04] p-2 transition-colors group-hover:bg-white/[0.07]">
        <BrandIcon templateId={template.id} size={40} />
      </div>

      {/* Name */}
      <h3 className="text-[13px] font-semibold text-white/90 transition-colors group-hover:text-teal-300">
        {template.name}
      </h3>

      {/* Description */}
      <p className="mt-1.5 text-[13px] leading-relaxed text-white/45 line-clamp-2">
        {template.description}
      </p>

      {/* Meta badges */}
      <div className="mt-3 flex items-center gap-1.5">
        <span className="rounded-full bg-white/[0.05] px-2 py-0.5 font-mono text-[9px] text-white/40">
          {AUTH_TYPE_LABELS[template.auth_type] || template.auth_type}
        </span>
        <span className="text-[9px] text-white/25">&middot;</span>
        <span className="font-mono text-[9px] text-white/35">
          {template.required_fields.length} field
          {template.required_fields.length !== 1 ? "s" : ""}
        </span>
        {template.openapi_url && (
          <>
            <span className="text-[9px] text-white/25">&middot;</span>
            <span className="text-[9px] font-medium text-teal-400/50">
              OpenAPI
            </span>
          </>
        )}
      </div>
    </div>
  );
}
